<template>
    <div id="app" :class="{ 'dark-mode': users.darkMode, 'new-theme': newTheme }" class="position-relative h-100 w-100">
        <SuspendComponent v-if="site && site.enable === false" />
        <LimitedModal
            v-if="!suspendModalVisible && shouldSelectPlanEnabled"
            @cancel="shouldSelectPlanEnabled = false"
        />
        <HeySupsis v-if="CONFIG.FEATURES.HEYSUPSIS_ENABLED" />
        <VisitorDetailPopup v-if="visitorDetailPopup" :visibleMail="false" />
        <WhatsappImportStatusViewer
            v-if="whatsappImportState && whatsappImportState.status === 'processing'"
            :state="whatsappImportState"
        />
        <WindowManager />
        <NewsModal v-if="isLoggedIn && isSiteSetupCompleted() && !isNeededShowResetPasswordModal()" />
        <router-view v-if="site != null && site._id != null" />
        <SetupWizard
            v-if="!isGuideTutorialsCompleted() && !isNeededShowResetPasswordModal()"
            :lobbyData="site.lobbyData"
            :createdDate="site.createdAt"
        />

        <a-modal
            :closable="false"
            :footer="null"
            :visible="multiSessionMode && isLoggedIn"
            centered
            class="multiSessionClass"
        >
            <div class="d-flex flex-column py-4">
                <p style="font-size: 20px">
                    {{ $t("message.multiModeAlertDesc") }}
                </p>
                <a-button class="align-self-center" size="large" type="primary" @click="reloadPage">
                    {{ $t("message.useHereText") }}
                </a-button>
            </div>
        </a-modal>
        <a-modal
            :closable="false"
            :footer="null"
            :title="$t('message.resetPassword')"
            :visible="isNeededShowResetPasswordModal() && !isSiteSetupCompleted()"
            v-if="!isSiteSetupCompleted()"
            class="multiSessionClass"
        >
            <ResetPassword :on-completed="$_.noop" />
        </a-modal>
        <a-modal
            :closable="false"
            :footer="null"
            :visible="userRoleUpdated && isLoggedIn"
            centered
            class="multiSessionClass"
        >
            <div class="d-flex flex-column py-4">
                <p style="font-size: 20px">
                    {{ $t("message.userRoleChanged") }}
                </p>
                <a-button class="align-self-center" size="large" type="primary" @click="forceToMainPath">
                    {{ $t("message.toMainPath") }}
                </a-button>
            </div>
        </a-modal>
        <a-modal
            id="fastWizardModal"
            :closable="false"
            :footer="null"
            :title="null"
            :visible="!isSiteSetupCompleted()"
            v-if="!isSiteSetupCompleted()"
            class="fastWizardModal"
            :style="{ width: '100%', height: '100%', margin: 0 }"
        >
            <!--             <FastWizard />-->

            <FastWizardV2 />
        </a-modal>
        <VideoCallScreen v-if="videoCallMode" />
        <OffLine class="h-100 w-100" />
        <UserChatChannelSideBar v-if="isLoggedIn && settings.privateChatEnabled && !isAdmin" />

        <ExternalWidgetViewer
            :title="widget.title"
            :url="widget.url"
            :visible="widget.visible"
            @close="handleWidgetClose"
            :fullScreen="widget.fullScreen"
            :mode="widget.mode"
            :html="widget.html"
        />

        <!-- WebPhone Components -->
        <WebPhoneRedSantral v-if="isSettingInitialized && !!user && webphoneKirmiziSantralChannelExists" />
        <WebPhoneNetGSM v-if="isSettingInitialized && !!user && webphoneNetgsmChannelExists" />
        <!-- Telnyx WebPhones - kullanıcının sahip olduğu her kanal için ayrı render -->
        <template v-if="isSettingInitialized && !!user && webphoneTelnyxChannelExists">
            <WebphoneTelnyx
                v-for="channel in userTelnyxChannels"
                :key="`telnyx-${channel._id}`"
                :channel-id="channel._id"
            />
        </template>

        <!-- Post-it Tahtası -->
        <PostitBoard
            v-if="$store.state.app.postitBoardVisible"
            @close="$store.commit('app/SET_POSTIT_BOARD_VISIBLE', false)"
        />

        <!-- Initial Message Modal -->
        <InitialMessageModal />

        <!-- Smart Hover Actions -->
        <SmartHoverActions />
    </div>
</template>

<script>
import "@/assets/scss/app.scss";
import "@/assets/scss/custom.scss";
import "ant-design-vue/dist/antd.css";
import "vue-material/dist/vue-material.min.css";
import "vue-material/dist/theme/default.css";
import "tippy.js/themes/light-border.css";
import "./assets/css/tippy-google.css";

import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";

import VueTippy from "vue-tippy";
import Antd from "ant-design-vue";
import VueMaterial from "vue-material";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import request from "./utils/request";
import { addMessage, sendSocketMessage, socketMessageStream } from "./utils/socketStream";
import Cookies from "js-cookie";
import SYSTEM_PUBSUB, { publishMessage, systemMessageStream, SystemMessageTypes } from "./utils/systemPubSub";
import { filter, map } from "rxjs/operators";
import { log2 } from "./utils/log2";
import moment from "moment";
import { sessionId } from "./utils/session";
import UserChatChannelSideBar from "./components/UserChatChannelSideBar";
import { getNextSocketServerCandidate, markFailedSocketServer } from "./api/hostFetcher";
import io from "socket.io-client";
import {
    newAnnouncementSound,
    newConversationSound,
    newPrivateMessage,
    newTicketSound,
    newVisitorSound,
    popAlertSound,
} from "./utils/notificationSounds";
import * as _ from "lodash";
import ResetPassword from "./components/ResetPassword";
import VideoCallScreen from "./components/VideoCallScreen";
import FastWizard from "./components/fast-initialize/FastWizard";
import FastWizardV2 from "./views/Pages/FastWizardV2.vue";
import { ColorPanel, ColorPicker } from "one-colorpicker";
import * as Sentry from "@sentry/vue";
import version from "./utils/version";
import { IS_DEV_ENVIRONMENT } from "./utils/utils";
import { WEB_PUSH_NOTIFICATION_SERVICE } from "./push-notification/subscribe";
import { getErrorReports } from "./api/errorReport";
import { ACTIVITY_API } from "./api/activity";
import OffLine from "./components/OffLine";
import NewsModal from "./components/common/NewsModal.vue";
import BillingWizard from "./views/Pages/Billing/components/old/BillingWizard.vue";
import CONFIG from "./config/config";
import VisitorDetailPopup from "./views/Pages/Visitors/VisitorDetailPopup";
import { sendDesktopNotification } from "./utils/desktopNotification";
import WindowManager from "./components/common/WindowManager.vue";
import HeySupsis from "./components/ai/HeySupsis.vue";

import ActivityTypes from "./views/Pages/Activity/ActivityTypes";
import WhatsappImportStatusViewer from "./views/Pages/WhatsappBusiness/import/WhatsappImportStatusViewer.vue";
import { pathPermissionChecker } from "./utils/pathPermissionChecker.js";
import RemoteDebugTool from "./utils/RemoteDebugTool";
import ChatWatcher from "./ChatWatcher";
import LimitedModal from "./views/Pages/Billing/components/old/LimitedModal.vue";
import SuspendedModal from "./views/Pages/Billing/components/old/SuspendedModal.vue";
import ExternalWidgetViewer from "./components/common/ExternalWidgetViewer.vue";
import SetupWizard from "./components/setup-wizard/SetupWizard.vue";
import WebPhoneRedSantral from "./views/Pages/Integrations/KirmiziSantral/WebPhoneRedSantral.vue";
import WebPhoneNetGSM from "./views/Pages/Integrations/NetGSM/WebPhoneNetGSM.vue";
import WebphoneTelnyx from "./views/Pages/Integrations/Telnyx/WebphoneTelnyx.vue";
import PostitBoard from "./components/note-board/PostitBoard.vue";
import InitialMessageModal from "./components/modals/InitialMessageModal.vue";
import SuspendComponent from "./components/common/SuspendComponent.vue";
import SmartHoverActions from "./components/SmartHoverActions.vue";

Vue.use(ColorPanel);
Vue.use(ColorPicker);
const OUTPUT_CONSOLE_VERSION = require("./utils/outputConsoleVersion");
const log = log2("App");
Vue.use(Antd);
Vue.use(VueMaterial);
Vue.use(VueTippy);

export default {
    name: "App",
    components: {
        SuspendedModal,
        LimitedModal,
        WhatsappImportStatusViewer,
        HeySupsis,
        BillingWizard,
        UserChatChannelSideBar,
        ResetPassword,
        VideoCallScreen,
        FastWizard,
        OffLine,
        FastWizardV2,
        NewsModal,
        VisitorDetailPopup,
        WindowManager,
        ExternalWidgetViewer,
        WebPhoneRedSantral,
        WebPhoneNetGSM,
        WebphoneTelnyx,
        SetupWizard,
        PostitBoard,
        InitialMessageModal,
        SuspendComponent,
        SmartHoverActions,
    },
    data() {
        return {
            currentPlanFetchStatus: false,
            shouldSelectPlanEnabled: false,
            suspendModalVisible: false,
            beep: Audio,
            currentSite: {},
            currentUrl: "",
            loginResponseWaiting: false,
            $socket: undefined,
            cancels: [],
            latency: 0,
            newTheme: true,
            currentStep: 0,
            userRoleUpdated: false,
            socketTryCount: 0,
            lastPlayedNewMessageSound: Date.now(),
            connectionInProgress: false,
            newVisitorSoundPlayed: [],
            lastLoggedUserId: null,
            whatsappImportState: null,
            CONFIG,
            widget: {
                visible: false,
                title: "",
                url: "",
                mode: "url",
                html: "",
            },
        };
    },
    computed: {
        ...mapState({
            privateChatIsMuted: (state) => state.app.privateChatIsMuted,
            chats: (state) => state.team.chats,
            teamChatVisible: (state) => state.team.teamChatVisible,
            selectedChat: (state) => state.team.selectedChat,
            emailDomain: (state) =>
                state.site.site.alias === "supsis"
                    ? CONFIG.EMAIL_DOMAIN
                    : `${state.site.site.alias}.${CONFIG.EMAIL_DOMAIN}`,
            language: (state) => state.language.language,
            settings: (state) => state.setting.settings,
            offlineMode: (state) => state.setting.offlineMode,
            isSettingInitialized: (state) => state.setting.initialized,
            plansAvailable: (state) => state.site.plansAvailable,
            users: (state) => state.user,
            user: (state) => state.auth.user,
            isRoot: (state) => _.get(state.auth.user, "role.isRoot", false),
            adminUser: (state) => state.auth.adminUser,
            isAdmin: (state) => state.auth.adminUser != null,
            loggedUserName: (state) =>
                state.auth.adminUser != null ? state.auth.adminUser.name : state.auth.user ? state.auth.user.name : "",
            conversations: (state) => state.chat.conversations,
            observing: (state) => state.chat.observing,
            activeConversation: (state) => state.chat.activeConversation,
            beepPlayedChatIds: (state) => state.chat.beepPlayedChatIds,
            isUserLoggedViaSocket: (state) => state.socketStatus.isUserLoggedViaSocket,
            socketConnected: (state) => state.socketStatus.socket_connected,
            socketStatus: (state) => ({
                socket_connected: state.socketStatus.socket_connected,
                connection_count: state.socketStatus.connection_count,
                current_socket: state.socketStatus.current_socket,
                isUserLoggedViaSocket: state.socketStatus.isUserLoggedViaSocket,
            }),
            site: (state) => state.site.site,
            zoom: (state) => state.layout.zoom,
            multiSessionMode: (state) => state.app.multiSessionMode,
            headerPopover: (state) => state.app.headerPopoverStatus,
            isChatHistoryOpen: (state) => state.chat.isChatHistoryOpen,
            myChatIds: (state) => state.chat.myChatIds,
            tabVisible: (state) => state.app.tabVisible,
            lastTabHiddenTS: (state) => state.app.lastTabHiddenTS,
            dialingVisitorId: (state) => state.webrtc.dialingVisitorId,
            videoCallMode: (state) => state.webrtc.videoCallMode,
            liveSupportLoading: (state) => state.app.liveSupportLoading,
            pendingMessages: (state) => state.chat.pendingMessages,
            waitingAnnouncements: (state) => state.announcements,
            planWizardCompleted: (state) => state.setting.settings?.planWizardCompleted,
            trialInfo: (state) => {
                const site = state.site.site;
                if (site) {
                    const { createdAt } = site;
                    const diff = Math.ceil(moment().diff(moment(createdAt), "days", true));
                    const tookDays = Math.max(diff, 0);
                    const isTrial = tookDays <= _.get(state.setting.settings, "plan.trialDays");
                    return {
                        trial: isTrial,
                        remainingDays: isTrial ? _.get(state.setting.settings, "plan.trialDays") - tookDays : 0,
                    };
                } else {
                    return { trial: false, remainingDays: 0 };
                }
            },
            tags: (state) => state.tag.tags.data,
            chatbotStoryViewActivity: (state) => state.app.chatbotStoryViewActivity,
            visitorDetailPopup: (state) => state.app.visitorDetailPopup,
            userInteracted: (state) => state.app.userInteracted,
            webphoneNetgsmChannelExists: (state) => state.channel.channels.some((x) => x.type === "webphone-netgsm"),
            webphoneTelnyxChannelExists: (state) => state.channel.channels.some((x) => x.type === "webphone-telnyx"),
            webphoneKirmiziSantralChannelExists: (state) =>
                state.channel.channels.some((x) => x.type === "webphone-kirmizisantral"),
        }),
        ...mapGetters({
            myActiveConversations: "chat/getMyActiveConversations",
            isLoggedIn: "auth/isLoggedIn",
            isHeaderPopoversOpen: "app/getPopoversStatus",
            shouldSelectPlan: "setting/shouldSelectPlan",
            isNotificationMute: "team/isNotificationMute",
            checkPermission: "auth/checkPermission",
            anyWhatsappChannelExists: "channel/anyWhatsappChannelExists",
        }),
        userTelnyxChannels() {
            const channels = this.$store.state.channel.channels;
            return channels.filter(
                (channel) => channel.type === "webphone-telnyx" && channel.payload?.userId === this.user?._id
            );
        },
    },
    methods: {
        ...mapActions({
            getStorageDetail: "site/getStorageDetail",
            setRoute: "app/setRoute",
            setBlockedConnection: "app/setBlockedConnection",
            updateChatLastMessage: "team/updateChatLastMessage",
            resetChatHistory: "chat/resetChatHistory",
            setTagsModules: "tag/setTags",
            getTags: "tag/getTags",
            getGlobalSettings: "getSetting",
            getSiteBySiteAddress: "site/getSiteBySiteAddress",
            setLanguage: "language/setLanguage",
            getSettings: "setting/getSettings",
            joinConversation: "chat/joinConversation",
            addMessageToChatHistory: "chat/addMessageToChatHistory",
            beepConversation: "chat/beepConversation",
            addConversation: "chat/addConversation",
            syncConversationWithIds: "chat/syncConversationWithIds",
            updateConversation: "chat/updateConversation",
            updateAgent: "user/updateAgent",
            setSocketStatus: "socketStatus/setSocketStatus",
            setSocket: "socketStatus/setSocket",
            userDeleted: "user/userDeleted",
            getUsers: "user/getUsers",
            getPermissions: "permission/getPermissions",
            setSettings: "setting/setSettings",
            setSubjects: "subject/setSubjects",
            offlineModeUpdated: "setting/offlineModeUpdated",
            changeTabVisibilityChange: "app/changeTabVisibilityChange",
            changeMultiSessionMode: "app/changeMultiSessionMode",
            refreshAuthUser: "auth/refreshAuthUser",
            togglePrivateChatVisibleState: "app/togglePrivateChatVisibleState",
            deleteContent: "content/deleteContent",
            contentUpdated: "content/contentUpdated",
            updateParent: "content/updateParent",
            getContents: "content/getContents",
            getBannedWords: "bannedWord/getBannedWords",
            getSubjects: "subject/getSubjects",
            getCustomerDomainsIfNeed: "visitor/getCustomerDomainsIfNeed",
            getCustomLoginFormInputs: "customLoginForm/getCustomLoginFormInputs",
            multiSetInputs: "customLoginForm/multiSetInputs",
            setChatFakeMode: "app/setChatFakeMode",
            setDebugMode: "app/setDebugMode",
            toggleMenu: "layout/toggleMenu",
            addCustomerDomain: "visitor/addCustomerDomain",
            setConversations: "chat/setConversations",
            setMultiVisitorAsParted: "visitor/setMultiVisitorAsParted",
            setConversationStats: "chat/setConversationStats",
            toggleChatHistory: "chat/toggleChatHistory",
            hideConversation: "chat/hideConversation",
            addUserToConversation: "chat/addUserToConversation",
            removeUserFromConversation: "chat/removeUserFromConversation",
            videoCallRejected: "webrtc/videoCallRejected",
            videoCallAnswered: "webrtc/videoCallAnswered",
            screenShare: "webrtc/screenShare",
            increaseUnreadMessageCount: "chat/increaseUnreadMessageCount",
            increaseUnreadTeamMessageCount: "team/increaseUnreadMessageCount",
            updateVisitorDetail: "chat/updateVisitorDetail",
            setLiveVisitorCount: "visitor/setLiveVisitorCount",
            viewConversation: "chat/viewConversation",
            setBugExist: "auth/setBugExist",
            updateInactiveState: "chat/updateInactiveState",
            getSiteCurrentPlans: "site/getSiteCurrentPlans",
            getBillingDetail: "site/getBillingDetail",
            getContactProperty: "contactProperty/getContactProperty",
            setPrivateChats: "team/setPrivateChats",
            setPrivateChatsSelectedChat: "team/setPrivateChatsSelectedChat",
            setUnreadChats: "team/setUnreadChats",
            getSitePlans: "site/getSitePlans",
            sendEmailSuccess: "mail/sendEmailSuccess",
            listSegments: "CB/listSegments",
            listStories: "CB/listStories",
            setchatbotStoryViewActivity: "app/setchatbotStoryViewActivity",
            listChannels: "channel/listChannels",
            getTemplates: "whatsapp/getTemplates",
            getAllUnreadMailCount: "mail/getAllUnreadMailCount",
            setUserInteracted: "app/setUserInteracted",
            updateMessageFromHistory: "chat/updateMessageFromHistory",
            updateMessagePartial: "chat/updateMessagePartial",
            setMessageAsDeletedFromHistory: "chat/setMessageAsDeletedFromHistory",
            getBalance: "wallet/getBalance",
            getWorkFlows: "tasks/getWorkFlows",
            getSiteFeatures: "site/getSiteFeatures",
            getConversationStatistics: "site/getConversationStatistics",
            setNotificationCount: "app/setNotificationCount",
            updateChatVisitorProfile: "chat/updateChatVisitorProfile",
            setMobileActive: "app/setMobileActive",
            setLastMessage: "chat/setLastMessage",
            getAgentsMailAddressList: "mail/getAgentsMailAddressList",
            fetchCustomerTables: "table/fetchCustomerTables",
            syncAutomations: "automation/syncAutomations",
            syncModules: "module/syncModules",
            syncForms: "forms/syncForms",
            fetchFilters: "favFilters/fetchFilters",
        }),
        ...mapMutations({
            calendarSuccess: "calendar/calendarSuccess",
            addNewCalendar: "calendar/addNewCalendar",
            addNewEvent: "calendar/addNewEvent",
            updateEventStore: "calendar/updateEvent",
            deleteEventStore: "calendar/deleteEvent",
            mutRemoteVideoEnabled: "webrtc/SET_REMOTE_VIDEO_ENABLED",
            mutRemoteMicEnabled: "webrtc/SET_REMOTE_MIC_ENABLED",
            setContactList: "whatsapp/setContacts",
            setWhatsappChannelContacts: "whatsapp/setChannelContacts",
            setWhatsappInfo: "whatsapp/setInfo",
            setWhatsappChannelInfo: "whatsapp/setChannelInfo",
            setInstagramContactList: "instagram/setContacts",
            setInstagramInfo: "instagram/setInfo",
            setTelegramBotInfo: "telegrambot/setInfo",
            updateVisitorLang: "chat/UPDATE_VISITOR_LANG",
            actPendingMessage: "chat/ACT_PENDING_MESSAGE",
            UPDATE_VISITOR_URL: "chat/UPDATE_VISITOR_URL",
            setWhatsappNumberDetail: "whatsapp/setMe",
            setChannelPhoneDetail: "whatsapp/setChannelPhoneDetail",
            domainIsCreated: "mail/domainIsCreated",
            closeNewMailModal: "mail/closeNewMailModal",
            updateMailAddress: "mail/updateMailAddress",
            playEmailNotification: "mail/playEmailNotification",
        }),
        closeSuspendedModal() {
            console.log("closeSuspendedModal");
            this.suspendModalVisible = false;
        },
        resizeListener() {
            const cb = () => {
                this.setMobileActive(window.innerWidth <= 500);
            };
            window.addEventListener("resize", cb);
            this.cancels.push(() => {
                window.removeEventListener("resize", cb);
            });
        },
        focusChat(conversationId) {
            const isChatPage = this.$route.path === "/chat";
            log("focusChat", {
                conversationId,
                isChatPage,
            });
            let delay = 500;
            if (!isChatPage) {
                this.$router.push("/chat");
                delay = 1000;
            }
            setTimeout(() => {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.OPEN_CHAT, {
                    conversationId,
                });
            }, delay);
        },
        subscribePushNotifications() {
            WEB_PUSH_NOTIFICATION_SERVICE.subscribe(this.site._id, this.user._id, "user_" + this.user._id);
            WEB_PUSH_NOTIFICATION_SERVICE.subscribe(this.site._id, this.user._id, "site_" + this.site._id);
            WEB_PUSH_NOTIFICATION_SERVICE.subscribe(this.site._id, this.user._id, "agents_" + this.site._id);
        },
        runWorkaroundSyncUserStatus() {
            if (!this.user.isOnline) {
                log("user offline", this.user);
                //workaround dağıtıkda offline olduğu biraz geç algılanıyo. REfresh anında online-offline-online sıralı aktivite olması gerekirken en son offline kalıyor
                setTimeout(() => {
                    this.refreshAuthUser();
                    sendSocketMessage("user login", {
                        userId: this.user._id,
                        siteId: this.site._id,
                        sessionId,
                        status: this.user.status,
                        checkMultiSession: sessionStorage.getItem("checkMultiSession") === "false" ? false : true,
                    });
                }, 2000);
            }
        },
        runInitialSocketQueries() {
            sendSocketMessage("user get-whatsapp-status", {
                siteId: this.site._id,
            });
            sendSocketMessage("user get-instagram-status", {
                siteId: this.site._id,
            });
            sendSocketMessage("telegram status", {
                siteId: this.site._id,
            });
            sendSocketMessage("whatsapp get-contacts", { siteId: this.site._id });
            sendSocketMessage("instagram get-users", { siteId: this.site._id });
        },
        handleSocketLoggedIn() {
            this.loginResponseWaiting = false;
            this.updateSocketStatus(true, true);
            this.runWorkaroundSyncUserStatus();
            this.runInitialSocketQueries();
            if (CONFIG.TEAM_CHAT.enabled) {
                this.listPrivateChats();
                this.listUnreadMessageCount();
            }
        },
        isNeededShowResetPasswordModal() {
            const forceResetPassword = this.$route.query.forceResetPassword === "true" && !!this.$route.query.ptoken;
            return this.isSiteSetupCompleted() && this.isLoggedIn && forceResetPassword && this.isSettingInitialized;
        },
        isSiteSetupCompleted() {
            if (!this.isLoggedIn) return true;
            if (!this.settings) return true;
            if (this.isAdmin) return true;
            if (!this.isSettingInitialized) return true;
            return this.settings.initialized;
        },
        isGuideTutorialsCompleted() {
            if (!this.isLoggedIn) return true;
            if (!this.settings) return true;
            if (this.isAdmin) return true;
            if (!this.isSettingInitialized) return true;
            if (!this.isRoot) return true;
            if (!this.site) return true;
            if (!this.site.lobbyData) return true;
            return this.user.isGuideTutorialsCompleted;
        },
        bindSocketEvents: function () {
            log.info("bindSocketEvents started");
            if (this.$socket) {
                log.debug("socket.io instance", this.$socket);
                const originalOnEventFunc = this.$socket.onevent;
                const self = this;
                if (!this.isAdmin) {
                    this.$socket.lastPingTS = Date.now();
                    this.$socket.healthCheckTimer = setInterval(() => {
                        if (Date.now() - this.$socket.lastPingTS > 15000) {
                            log.warn("socket.io health check failed", this.$socket.connected);
                            if (!this.$socket.connected) {
                                this.socketDisconnectCallback();
                            }
                            this.$socket.close();
                        }
                    }, 5000);
                    this.$socket.onevent = async function () {
                        if (self.multiSessionMode) return;
                        try {
                            const data = arguments[0].data;
                            let cmd = data[0];
                            log.debug(`socket event ${cmd}`, data);
                            const maybeSiteId = cmd.split("-").length > 1 ? _.last(cmd.split("-")) : undefined;
                            cmd = _.head(cmd.split("-"));

                            if (maybeSiteId && maybeSiteId !== self.site._id) {
                                log.error("SHOULD NOT BE HAPPEN, other site socket message received", {
                                    cmd,
                                    maybeSiteId,
                                    currentSiteID: self.site._id,
                                });
                                return;
                            }
                            // log.debug('socket server message', cmd);
                            if (cmd.indexOf("multi_visitor_update_part") !== -1) {
                                const allPartCompleted = cmd === "multi_visitor_update_part_finished";
                                if (allPartCompleted) return;
                                const visitors = data[1];
                                const meta = cmd
                                    .replace("multi_visitor_update_part_", "")
                                    .split("_of_")
                                    .map((x) => parseInt(x));
                                const currentPart = _.head(meta);
                                const totalPartCount = _.last(meta);
                                if (totalPartCount - currentPart === 0) {
                                    log.info("Multi Visitors All Part Completed");
                                } else {
                                    log.info(
                                        `Multi Visitors Part ${currentPart} Received , Waiting = ${
                                            totalPartCount - currentPart
                                        }`
                                    );
                                }

                                self.setMultiVisitorAsParted({
                                    visitors,
                                    partNumber: currentPart,
                                });
                            } else if (cmd === "ping") {
                                self.$socket.lastPingTS = Date.now();
                                sendSocketMessage("pong", {
                                    type: "user",
                                    siteId: self.site._id,
                                    userId: self.user._id,
                                    status: self.user.status,
                                    isOnline: true,
                                });
                            } else if (cmd === "resend_login") {
                                sendSocketMessage("user login", {
                                    userId: self.user._id,
                                    siteId: self.site._id,
                                    sessionId,
                                    status: self.user.status,
                                    checkMultiSession:
                                        sessionStorage.getItem("checkMultiSession") === "false" ? false : true,
                                });
                            } else if (cmd === "ticket_message") {
                                if (self.userInteracted) {
                                    publishMessage("ticket_message", data[1]);
                                    newTicketSound().play();
                                }
                            } else if (cmd === "new_announcement" && this.userInteracted) {
                                newAnnouncementSound().play();
                            } else if (cmd === "message_was_sent") {
                                self.actPendingMessage(data[1].uniqMessageId);
                                addMessage(cmd, data[1]);
                            } else if (cmd === "message_updated") {
                                addMessage(cmd, data[1]);
                                self.updateMessageFromHistory(data[1].conversation, data[1]);
                            } else if (cmd === "message_updated_partial") {
                                addMessage(cmd, data[1]);
                                self.updateMessagePartial(data[1]);
                            } else if (cmd === "message_deleted") {
                                addMessage(cmd, data[1]);
                                console.log("message_deleted", data[1]);
                                self.setMessageAsDeletedFromHistory({
                                    conversationId: data[1].conversationId,
                                    uniqMessageId: data[1].uniqMessageId,
                                });
                            } else if (cmd === "whatsapp_service_status") {
                                const { status, channelId } = data[1];
                                log.debug("whatsapp_service_status", status, channelId);
                                if (channelId) self.setWhatsappChannelInfo({ status, channelId });
                                else self.setWhatsappInfo(data[1].status);
                            } else if (cmd === "instagram_service_status") {
                                self.setInstagramInfo(data[1].status);
                            } else if (cmd === "telegrambot_service_status") {
                                self.setTelegramBotInfo(data[1].status);
                            } else if (cmd === "translate_message") {
                                publishMessage("translate_message", data[1]);
                            } else if (cmd === "time_sync") {
                                const server_now = data[1].now;
                                const client_now = Date.now();

                                if (self.lastTimeSync && self.last_server_now) {
                                    const period = server_now - self.last_server_now;
                                    if (self.lastSyncPeriod) {
                                        self.latency = Math.max(period - (client_now - self.lastTimeSync), 0);
                                    }
                                    self.lastSyncPeriod = period;
                                }

                                const diff = client_now - server_now;
                                log("TIME_DIFF_WITH_SERVER", {
                                    ms: diff,
                                    sec: diff / 1000,
                                    // latency: self.latency,
                                    // period:server_now-self.last_server_now,
                                    // lastTimeSync: self.lastTimeSync,
                                    // last_server_now: self.last_server_now
                                });
                                self.last_server_now = server_now;
                                self.lastTimeSync = client_now;
                            } else if (cmd === "live_conversations_stats") {
                                const { stats } = data[1];
                                self.setConversationStats(stats);
                            } else if (cmd === "team_list_chats_response") {
                                const { chats } = data[1];
                                await self.setPrivateChats(chats);
                                chats.forEach((chat) => {
                                    self.joinPrivateChat(chat._id);
                                });
                            } else if (cmd === "team_list_chats_selected_chat_response") {
                                const { chats } = data[1];
                                await self.setPrivateChatsSelectedChat(chats);
                                chats.forEach((chat) => {
                                    self.joinPrivateChat(chat._id);
                                });
                            } else if (cmd === "team_list_unread_chats_response") {
                                const { chats } = data[1];
                                await self.setUnreadChats(chats);
                            } else if (cmd === "new_mail") {
                                self.playEmailNotification(true);
                                self.getAllUnreadMailCount(self.user._id);
                            } else if (cmd === "forward_mail_added") {
                                self.getAgentsMailAddressList();
                            } else if (cmd === "team_chats_selected_chat_updated") {
                                self.listPrivateSelectedChatChats();
                            } else if (cmd === "team_chats_updated") {
                                self.listPrivateChats();
                            } else if (cmd === "email_domain_is_created") {
                                const payload = data[1];
                                self.domainIsCreated(payload);
                            } else if (cmd === "email_send_successful") {
                                const { email, requestId } = data[1];
                                addMessage(cmd, data[1]);
                                await self.sendEmailSuccess(email);
                            } else if (cmd === "email_update_user_departments") {
                                await self.getAgentsMailAddressList();
                            } else if (cmd === "add_sender_mail") {
                                await self.getAgentsMailAddressList();
                            } else if (cmd === "new_mail_address") {
                                self.getAgentsMailAddressList();
                                addMessage(cmd, data[1]);
                            } else if (cmd === "update_sender_mail_verify") {
                                const { result } = data[1];
                                self.updateMailAddress(result);
                            } else if (cmd === "multi_conversation_add") {
                                const { conversations } = data[1];
                                self.setConversations({
                                    conversations,
                                    partNumber: 1,
                                }).then(() => {
                                    self.rejoinChats();
                                });
                            } else if (cmd === "new_visitor_connected") {
                                let visitorIds = data[1];
                                visitorIds = Array.isArray(visitorIds) ? visitorIds : [{ visitorId: visitorIds }];
                                self.playNewVisitorSound(visitorIds);
                                visitorIds.forEach((x) =>
                                    publishMessage("new_visitor_connected", {
                                        visitorId: x.visitorId,
                                    })
                                );
                                addMessage(cmd, data[1]);
                            } else if (cmd === "visitor_offline") {
                                const visitorId = data[1];
                                const visitorIds = Array.isArray(visitorId) ? visitorId : [visitorId];
                                // self.removeNewVisitorPlayedSound(visitorIds);
                                visitorIds.forEach((id) => publishMessage("visitor_offline", { id }));
                                // publishMessage('visitor_offline', { id: visitorId })
                            } else if (cmd === "visitor_lang_change") {
                                self.updateVisitorLang(data[1]);
                            } else if (cmd === "conversation_destroy") {
                                const id = data[1];
                                const ids = Array.isArray(id) ? id : [id];
                                ids.forEach((id) => publishMessage("conversation_destroy", { id }));
                                // publishMessage('conversation_destroy', { id })
                                addMessage(cmd, data[1]);
                            } else if (cmd === "conversation_add") {
                                const conversation = data[1];
                                log.debug("conversation_add", conversation);
                                self.conversation_add(conversation);
                                self.join(conversation, true, true, true);
                                if (
                                    self.lastImmediatelyJoinedChat &&
                                    self.lastImmediatelyJoinedChat === conversation._id
                                ) {
                                    self.viewConversation({ id: conversation._id });
                                }
                            } else if (cmd === "whatsapp_contacts") {
                                const whatsappContacts = _.get(data[1], "response.response.response");
                                const whatsappContactsTotal = _.get(data[1], "total");
                                const channelId = _.get(data[1], "channelId");
                                if (channelId) {
                                    self.setWhatsappChannelContacts({
                                        channelId,
                                        contacts: whatsappContacts,
                                        total: whatsappContactsTotal,
                                    });
                                } else {
                                    // self.setContactList({ whatsappContacts, whatsappContactsTotal });
                                }
                            } else if (cmd === "instagram_contacts") {
                                const contacts = _.get(data[1], "response.response.response");
                                self.setInstagramContactList(contacts);
                            } else if (cmd === "user_update") {
                                const agent = data[1];
                                log("user_update work start", {
                                    socketAgent: agent,
                                    authAgent: self.user,
                                });
                                self.updateAgent(agent);
                                if (agent.userId === self.user._id && agent.role) {
                                    self.refreshAuthUser().then(() => {
                                        const permission = self.user.role.permissions.find(
                                            (p) => p.name === self.$route.meta.permission
                                        );
                                        log("this permission in here ", permission);
                                        //check if user currently permitted page if not redirect to main page
                                        if (!permission && !self.user.role.isRoot) {
                                            self.userRoleUpdated = true;
                                        }
                                        log("user_update work end", {
                                            socketAgent: agent,
                                            authAgent: self.user,
                                        });
                                    });
                                }
                            } else if (cmd === "live_visitors_count") {
                                self.setLiveVisitorCount();
                            } else if (cmd === "user_deleted") {
                                const { userId } = data[1];
                                self.userDeleted(userId);
                            } else if (cmd === "custom-login-form-updated") {
                                const { inputs } = data[1];
                                self.multiSetInputs(inputs);
                            } else if (cmd === "new_content_added") {
                                //if new content added users fetch contents again
                                self.getContents({});
                            } else if (cmd === "content_moved") {
                                self.getContents({});
                            } else if (cmd === "content_parent_moved") {
                                self.getContents({});
                            } else if (cmd === "content_updated") {
                                const { content } = data[1];
                                self.contentUpdated(content);
                            } else if (cmd === "content_deleted") {
                                const { contentId } = data[1];
                                log("default content deleted", { contentId });
                                self.deleteContent({
                                    id: contentId,
                                    sendToBackEnd: false,
                                });
                            } else if (cmd === "bannedWord_deleted") {
                                self.getBannedWords({});
                            } else if (cmd === "bannedWord_updated") {
                                self.getBannedWords({});
                            } else if (cmd === "bannedWord_added") {
                                self.getBannedWords({});
                            } else if (cmd === "multi_session") {
                                if (sessionStorage.getItem("checkMultiSession") === "false") {
                                    return;
                                }
                                const isMultiSession = data[1];
                                self.changeMultiSessionMode({ isMultiSession });
                                // if (isMultiSession) {
                                //     self.$router.push('/coklu-oturum')
                                // }
                            } else if (cmd === "settings_updated") {
                                const settings = data[1];
                                self.setSettings(settings);
                            } else if (cmd === "subjects_changed") {
                                const { subjects } = data[1];
                                self.setSubjects(subjects);
                            } else if (cmd === "tags_changed") {
                                const { tags } = data[1];
                                self.setTagsModules(tags);
                            } else if (cmd === "global_settings_updated") {
                                log("global settings is here ");
                                self.getGlobalSettings(data[1]);
                            } else if (cmd === "create_self_service_site_response") {
                                addMessage(cmd, data[1]);
                            } else if (cmd === "user_join_story") {
                                const { userId, storyId } = data[1];
                                log("user_join_story", { userId, storyId });
                                const activity = self.chatbotStoryViewActivity.findIndex((a) => a.userId === userId);
                                if (activity < 0) {
                                    self.setchatbotStoryViewActivity([
                                        ...self.chatbotStoryViewActivity,
                                        { userId, storyId, updateTS: Date.now() },
                                    ]);
                                } else {
                                    self.setchatbotStoryViewActivity(
                                        self.chatbotStoryViewActivity.map((x) => {
                                            if (x.userId === userId) {
                                                x.updateTS = Date.now();
                                                x.storyId = storyId;
                                            }
                                            return x;
                                        })
                                    );
                                }
                            } else if (cmd === "resync_channels") {
                                const { channelId } = data[1];
                                self.retryFetch(
                                    "listChannels",
                                    () => {
                                        return self.listChannels();
                                    },
                                    3
                                );
                            } else if (cmd === "offline_mode_updated") {
                                const { offlineMode } = data[1];
                                self.offlineModeUpdated({ offlineMode });
                            } else if (cmd === "conversation_visitor_info_updated") {
                                const { conversationId, fullname, email } = data[1];
                                self.updateVisitorDetail({ conversationId, fullname, email });
                            } else if (cmd === "conversation_sync") {
                                const { ids } = data[1];
                                self.syncConversationWithIds(ids);
                            } else if (cmd === "video_call_rejected") {
                                const { visitorId } = data[1];
                                log("vc_rejected received", data[1]);
                                if (self.dialingVisitorId === visitorId) {
                                    publishMessage("vc_rejected", {});
                                    self.videoCallRejected();
                                }
                            } else if (cmd === "screen_share") {
                                const { isStartCall, soundOnly } = data[1];
                                log("screen share is here ");
                                self.screenShare(isStartCall);
                            } else if (cmd === "video_call_accepted") {
                                const { visitorId } = data[1];
                                if (self.dialingVisitorId === visitorId) {
                                    self.videoCallAnswered();
                                }
                            } else if (cmd === "toggle_video_call") {
                                self.mutRemoteMicEnabled(data[1].mic);
                                self.mutRemoteVideoEnabled(data[1].video);
                            } else if (cmd === "live_visitors_count") {
                                self.setLiveVisitorCount();
                            } else if (cmd === "logged_user") {
                                self.handleSocketLoggedIn();
                            } else if (cmd === "user_login_successful") {
                                self.refreshAuthUser();
                            } else if (cmd === "visitor_url_changed") {
                                self.UPDATE_VISITOR_URL(data[1]);
                                addMessage(cmd, data[1]);
                            } else if (cmd === "conversation_inactive_status_changed") {
                                const { conversationId, inactive } = data[1];
                                self.updateInactiveState({ conversationId, inactive });
                            } else if (cmd === "calendar_event_created") {
                                console.log("calendar_event_created app..", data[1]);
                                const { events } = data[1];
                                self.addNewEvent(events);
                            } else if (cmd === "calendar_event_notification") {
                                console.log("calendar_event_notification app..", data[1]);
                                newAnnouncementSound().play();
                                // const { event } = data[1];
                                // self.updateCalendarEvents(event);
                            } else if (cmd === "calendar_success") {
                                console.log("calendar_success app..", data[1]);
                                const { userOAuth } = data[1];
                                self.calendarSuccess(userOAuth);
                            } else if (cmd === "new_calendar") {
                                const { calendar } = data[1];
                                self.addNewCalendar(calendar);
                            } else if (cmd === "calendar_event_updated") {
                                const { event } = data[1];
                                self.updateEventStore(event);
                            } else if (cmd === "calendar_event_delete") {
                                const { event } = data[1];
                                self.deleteEventStore(event);
                            } else if (cmd === "reservation_event_created") {
                                const { event } = data[1];
                                console.log("App reservation_event_created", { event });
                                self.addNewEvent(event);
                            } else if (cmd === "whatsapp_service_response") {
                                const { response, path, success, channelId } = data[1];

                                if (path === "getMe" && success) {
                                    if (channelId) {
                                        self.setChannelPhoneDetail({
                                            payload: response,
                                            channelId,
                                        });
                                    } else {
                                        self.setWhatsappNumberDetail(response);
                                    }
                                }
                                addMessage(cmd, data[1]);
                            } else if (cmd === "whatsapp_import_state") {
                                const state = data[1];
                                self.whatsappImportState = state;
                            } else if (cmd === "debug_agent_request") {
                                const { messageId, command, payload } = data[1];
                                RemoteDebugTool.create()
                                    .runCommand(self.siteId, messageId, command, payload)
                                    .catch(console.error);
                            } else if (cmd === "new_notification") {
                                self.lastNotification = data[1];
                                const { notification } = data[1];
                                const activityType = notification.activityType;
                                const activity = Object.values(ActivityTypes).find((x) => x.id === activityType);
                                if (activity) {
                                    const message = activity.message(notification.activity, self);
                                    popAlertSound.play();
                                    Vue.$toast(message.replace(/<b>|<\/b>/g, ""), {
                                        position: "bottom-right",
                                        timeout: 2500,
                                        closeOnClick: true,
                                        pauseOnFocusLoss: true,
                                        pauseOnHover: true,
                                        draggable: true,
                                        draggablePercent: 0.6,
                                        showCloseButtonOnHover: false,
                                        hideProgressBar: true,
                                        closeButton: "button",
                                        icon: {
                                            iconClass: "material-icons", // Optional
                                            iconChildren: activity.icon, // Optional
                                            iconTag: "i", // Optional
                                        },
                                        rtl: false,
                                    });
                                }

                                addMessage(cmd, data[1]);
                            } else if (cmd === "message_receipt") {
                                self.setLastMessage(data[1]);
                            } else if (cmd === "blocked_connection") {
                                self.$router.push("/blocked");
                                self.setBlockedConnection(true);
                            } else if (cmd === "broadcast_status") {
                                addMessage(cmd, data[1]);
                            } else if (cmd === "refresh_templates") {
                                const { channelId } = data[1];
                                self.getTemplates(channelId);
                            } else if (cmd === "automation_show_widget") {
                                const { title, url, fullScreen, mode, html, sessionId: _sessionId } = data[1];
                                const isTabVisible = self.tabVisible;
                                if ((!!_sessionId && _sessionId === sessionId) || isTabVisible) {
                                    self.widget = {
                                        ...self.widget,
                                        title,
                                        url,
                                        visible: true,
                                        fullScreen,
                                        mode: mode || "url", // Geriye dönük uyumluluk için default "url"
                                        html: html || "",
                                    };
                                }
                            } else if (cmd === "open_panel_behind_link") {
                                const { link, clientAddress, targetSiteId, targetUserId, userEmail, success } = data[1];
                                if (success) {
                                    self.widget = {
                                        ...self.widget,
                                        title: `${userEmail} - ${clientAddress}`,
                                        url: link,
                                        visible: true,
                                        fullScreen: true,
                                    };
                                }
                            } else if (cmd === "start_chat_with_supsis_team") {
                                publishMessage(SystemMessageTypes.CONNECT_SUPPORT_TEAM, {
                                    initialMessage: "Supsis destek ekibiyle görüşmek istiyorum",
                                    largeMode: true,
                                    connectingUserId: data[1].connectingUserId,
                                });
                            } else {
                                /*log.debug("unhandled message ", cmd)*/
                                addMessage(cmd, data[1]);
                            }
                        } catch (e) {
                            console.error("error", e);
                        } finally {
                            originalOnEventFunc.call(self.$socket, ...arguments);
                        }
                    };
                } else {
                    this.$socket.onevent = function () {
                        try {
                            const data = arguments[0].data;
                            let cmd = data[0];
                            const maybeSiteId = cmd.split("-").length > 1 ? _.last(cmd.split("-")) : undefined;
                            cmd = _.head(cmd.split("-"));
                            if (maybeSiteId && maybeSiteId !== self.site._id) {
                                log.error("SHOULD NOT BE HAPPEN, other site socket message received", {
                                    cmd,
                                    maybeSiteId,
                                    currentSiteID: self.site._id,
                                });
                                return;
                            }
                            log.debug("Socket Event: ", cmd);
                            if (cmd === "logged_admin") {
                                self.loginResponseWaiting = false;
                                self.updateSocketStatus(true, true);

                                publishMessage("admin-logged", {});
                                sendSocketMessage("user get-whatsapp-status", {
                                    siteId: self.site._id,
                                });
                                sendSocketMessage("user get-instagram-status", {
                                    siteId: self.site._id,
                                });
                            } else if (cmd === "whatsapp_service_status") {
                                const { status, channelId } = data[1];
                                if (channelId) self.setWhatsappChannelInfo({ status, channelId });
                                else self.setWhatsappInfo(data[1].status);
                            } else if (cmd === "instagram_service_status") {
                                self.setInstagramInfo(data[1].status);
                            } else if (cmd === "telegrambot_service_status") {
                                self.setTelegramBotInfo(data[1].status);
                            } else if (cmd === "global_settings_updated") {
                                log("global settings is here ");
                                self.getGlobalSettings(data[1]);
                            } else {
                                /*log.debug("unhandled message ", cmd)*/
                                addMessage(cmd, data[1]);
                            }
                        } catch (e) {
                            console.error("error", e);
                        } finally {
                            originalOnEventFunc.call(self.$socket, ...arguments);
                        }
                    };
                }
            }

            this.$socket.on("connect", () => {
                log.debug("socket connected -->");
                this.updateSocketStatus(true, false);

                this.loginResponseWaiting = false;
                if (this.site && this.site._id != null) {
                    this.login();
                    _.sortBy(this.pendingMessages, "createdDate").forEach((msg) => {
                        sendSocketMessage("user message", msg);
                    });
                }
                this.socketTryCount = 0;
            });
            this.$socket.on("disconnect", () => {
                this.socketDisconnectCallback();
            });
        },
        socketDisconnectCallback() {
            sendSocketMessage("user logging-out-reason", {
                userId: this.user._id,
                siteId: this.site._id,
                reason: "socket-disconnect",
            });
            this.connectionInProgress = false;
            log.warn("socket disconnected -->", this.connectedSocketServerId);
            this.updateSocketStatus(false, false);

            this.loginResponseWaiting = false;
            if (this.connectedSocketServerId) {
                markFailedSocketServer(this.connectedSocketServerId);
                setTimeout(() => {
                    this.startSocketIOConnection();
                }, Math.ceil(Math.random() * 2 * 1000));
            }
            if (this.$socket.healthCheckTimer) {
                clearInterval(this.$socket.healthCheckTimer);
            }
        },
        disconnectSocket(reconnect = false, reason) {
            log.warn("disconnectSocket", { reconnect });
            const socket = this.$socket;
            sendSocketMessage("user logging-out-reason", {
                userId: this.user._id,
                siteId: this.site._id,
                reason,
            });
            if (socket) socket.close();
        },
        conversation_add(conversation, forward) {
            conversation.users = (conversation.users || []).map((x) => (typeof x === "object" ? x._id : x));
            const meta = {
                isOnline: conversation.status !== "destroyed",
            };

            let c = this.conversations.find((c) => c._id === conversation._id);
            if (!c) {
                meta["active"] = true;
                meta["unread"] = 0;
                meta.isLooked = false;
                meta.autoAssign = true;
                log.debug("adding", { conversation });
                this.addConversation({ conversation, meta });
                if (conversation.homeSite) this.addCustomerDomain(conversation.homeSite);
            } else {
                log.debug("updating", { conversation });
                conversation.isLooked = c.isLooked;
                this.updateConversation(conversation);
            }
            const isMeContain = _.findIndex(conversation.users, (q) => q === this.user._id) >= 0;
            const isMeFirstConnected = conversation.connectedUser === this.user._id; // acaba chat el değiştirdiğinde çalmasını istiyormuyuz
            const beepPlayed = this.beepPlayedChatIds.includes(conversation._id);
            const beepRequired = forward || (conversation.status === "active" && isMeContain && !beepPlayed);
            log("conversation_add new -->", {
                status: conversation.status,
                isMeContain,
                isMeFirstConnected,
                beepPlayed,
                beepRequired,
                cId: conversation._id,
                userId: this.user._id,
                visitor: conversation.fullname,
            });

            if (beepRequired) {
                this.playBeep(conversation._id);
            }
        },
        get() {
            log.info("get started");
            const getSiteByCustomerClientAddress = (resolve) => {
                this.currentUrl = window.location.hostname;
                return this.getSiteBySiteAddress(this.currentUrl).then((response) => {
                    request.defaults.headers.common["site-id"] = response.data._id;
                    if (this.language == null) {
                        this.getSettings().then(() => {
                            this.setLanguage(this.settings.language.clientDefault);
                        });
                    } else {
                        this.$i18n.locale = this.language.code;
                    }
                    this.currentSite = response.data;

                    resolve();
                });
            };
            return new Promise((resolve) => {
                getSiteByCustomerClientAddress(resolve).catch((err) => {
                    log.error("customer site fetch error", err);
                    setTimeout(() => {
                        this.get().then(resolve);
                    }, Math.floor(Math.random() * 3 * 1000));
                });
            });
        },
        joinPrivateChat(conversationId) {
            sendSocketMessage("team join-conversation", {
                userId: this.user._id,
                conversationId: conversationId,
                siteId: this.site._id,
            });
        },

        init() {
            log("init started");
            const p = this.get();
            if (this.isLoggedIn) {
                p.then(() => {
                    this.startSocketIOConnection();
                    if (!this.users.initialized) {
                        log.info("user initializing now");
                        this.getUsers({ perPage: 1000, page: 1 });
                    }
                    if (!this.isSettingInitialized) {
                        this.getSettings().then(() => {
                            if (this.shouldSelectPlan.enabled && !this.isAdmin) this.$router.push("/settings/billing");
                        });
                    }
                    if (this.currentSite.suspend) this.$router.push("/settings/billing");
                });
            }
        },
        join(conversation, minimized = false, autoAssign = false, sendToSocket = false) {
            this.joinConversation({
                conversation,
                observe: false,
                minimized: minimized,
                autoAssign: autoAssign,
                sendToSocket,
            });
        },
        login() {
            if (this.multiSessionMode) return;
            log.info("Version", {
                a: OUTPUT_CONSOLE_VERSION,
            });
            log.debug("login called", {
                socketConnected: this.socketConnected,
                isUserLoggedViaSocket: this.isUserLoggedViaSocket,
                loginResponseWaiting: this.loginResponseWaiting,
            });
            if (this.socketConnected && !this.isUserLoggedViaSocket && !this.loginResponseWaiting) {
                this.loginResponseWaiting = true;
                if (this.isAdmin) {
                    this.setLoginTimeout();
                    sendSocketMessage("admin login", {
                        userId: this.adminUser._id,
                        siteId: this.site._id,
                        sessionId,
                    });
                } else {
                    this.setLoginTimeout();
                    log("user login sending", { status: this.user.status, user: this.user });
                    sendSocketMessage("user login", {
                        userId: this.user._id,
                        siteId: this.site._id,
                        sessionId,
                        status: this.user.status,
                        checkMultiSession: sessionStorage.getItem("checkMultiSession") === "false" ? false : true,
                    });
                }
                if (!this.isAdmin) {
                    ChatWatcher.create();
                }
            }
        },
        playBeep: _.debounce(function (id) {
            return; //todo bunu artık chatwatcher yapacak
            if (id) this.beepConversation({ id });
            // sendDesktopNotification(this.$t("message.newConversationAlert"), 0);
            if (this.userInteracted) {
                newConversationSound().play();
            }
        }, 1000),
        rejoinChats() {
            if (this.myActiveConversations !== null && this.myActiveConversations.length > 0) {
                for (let conversation of this.myActiveConversations) {
                    if (!this.observing.includes(conversation._id)) {
                        if (conversation.status !== "destroyed") {
                            this.join(conversation, true, true, true);
                        }
                    }
                }
            }
        },
        reloadPage() {
            window.location.reload();
        },
        setLoginTimeout() {
            if (this.loginTimer) clearInterval(this.loginTimer);
            this.loginTimer = setTimeout(() => {
                if (!this.isUserLoggedViaSocket) {
                    log.warn("ws user not logged in 10sec , reconnecting ws");
                    this.disconnectSocket(false, "login-timeout");
                }
            }, 10000);
        },
        showIntroToggleMenu(stepNumber) {
            // target = menu || statistikMenu || privateChat || help |

            if (stepNumber === 0) {
                setTimeout(() => {
                    document.getElementById("nextStepButton").disabled = true;
                }, 100);
                setTimeout(() => {
                    if (this.currentStep === 0) document.getElementById("statPanelSlider").click();
                    setTimeout(() => {
                        if (this.currentStep === 0) {
                            document.getElementById("statPanelSlider").click();
                            document.getElementById("nextStepButton").disabled = false;
                        }
                    }, 1000);
                }, 1000);
            } else if (stepNumber === 1) {
                setTimeout(() => {
                    document.getElementById("nextStepButton").disabled = true;
                }, 100);
                setTimeout(() => {
                    if (this.currentStep === 1) this.toggleMenu();
                    setTimeout(() => {
                        if (this.currentStep === 1) this.toggleMenu();
                        document.getElementsByClassName("v-step")[0].style.left = "0px";
                        document.getElementById("nextStepButton").disabled = false;
                    }, 2000);
                    document.getElementsByClassName("v-step")[0].style.left = "190px";
                    document.getElementsByClassName("v-step")[0].style.zIndex = "999999";
                }, 100);
            } else if (stepNumber === 2) {
                setTimeout(() => {
                    document.getElementById("nextStepButton").disabled = true;
                }, 100);
                setTimeout(() => {
                    if (this.currentStep === 2) this.togglePrivateChatVisibleState();
                    setTimeout(() => {
                        if (this.currentStep === 2) this.togglePrivateChatVisibleState();
                        document.getElementsByClassName("v-step")[0].style.left = "0px";
                        document.getElementById("nextStepButton").disabled = false;
                    }, 2000);
                    document.getElementsByClassName("v-step")[0].style.left = "-220px";
                }, 1000);
            } else if (stepNumber === 3) {
                setTimeout(() => {
                    document.getElementById("nextStepButton").disabled = true;
                }, 100);
                setTimeout(() => {
                    if (this.currentStep === 3) document.getElementById("popoverHelpMenu").click();
                    setTimeout(() => {
                        if (this.currentStep === 3) document.getElementById("popoverHelpMenu").click();
                        document.getElementsByClassName("v-step")[0].style.left = "0px";
                        document.getElementById("nextStepButton").disabled = false;
                    }, 2000);
                    document.getElementsByClassName("v-step")[0].style.left = "-70px";
                }, 1000);
            } else if (stepNumber === 4) {
                setTimeout(() => {
                    document.getElementById("nextStepButton").disabled = true;
                }, 100);
                setTimeout(() => {
                    if (this.currentStep === 4) document.getElementById("settingsMenu").click();
                    setTimeout(() => {
                        if (this.currentStep === 4) document.getElementById("settingsMenu").click();
                        document.getElementsByClassName("v-step")[0].style.left = "0px";
                        document.getElementById("nextStepButton").disabled = false;
                    }, 2000);
                    document.getElementsByClassName("v-step")[0].style.left = "-70px";
                }, 1000);
            } else if (stepNumber === 5) {
                setTimeout(() => {
                    document.getElementById("nextStepButton").disabled = true;
                }, 100);
                setTimeout(() => {
                    if (this.currentStep === 5) document.getElementById("profileMenu").click();
                    setTimeout(() => {
                        if (this.currentStep === 5) document.getElementById("profileMenu").click();
                        document.getElementsByClassName("v-step")[0].style.left = "0px";
                        document.getElementById("nextStepButton").disabled = false;
                    }, 2000);
                    document.getElementsByClassName("v-step")[0].style.left = "-80px";
                }, 1000);
            } else if (stepNumber === 6) {
                document.getElementById("tabVisitors").click();
            } else if (stepNumber === 7) {
                document.getElementById("tabVisitorHistory").click();
            } else if (stepNumber === 8) {
                document.getElementById("tabVisitorRepresentative").click();
                setTimeout(() => {
                    document.getElementById("tabVisitors").click();
                }, 1000);
            } else if (stepNumber === 9) {
            } else if (stepNumber === 10) {
                document.getElementById("tabLSiteVisitors").click();
                setTimeout(() => {
                    document.getElementById("tabLLiveSupport").click();
                }, 1000);
            } else if (stepNumber === 10) {
                document.getElementById("tabLSiteVisitors").click();
                setTimeout(() => {
                    document.getElementById("tabLLiveSupport").click();
                }, 1000);
            } else if (stepNumber === 15) {
                setTimeout(() => {
                    document.getElementById("nextStepButton").disabled = true;
                    setTimeout(() => {
                        document.getElementById("joinChat").click();
                        document.getElementsByClassName("v-step")[0].style.display = "none";
                    }, 2000);
                }, 100);
            } else if (stepNumber === 16) {
                setTimeout(() => {
                    if (this.currentStep === 16) document.getElementById("actionSettingsButton").click();
                    document.getElementById("prevStepButton").disabled = true;
                    setTimeout(() => {
                        if (this.currentStep === 16) document.getElementById("actionSettingsButton").click();
                    }, 2000);
                }, 100);
            } else if (stepNumber === 19) {
                setTimeout(() => {
                    if (this.currentStep === 19) document.getElementById("fakeDefaultContents").click();
                    setTimeout(() => {
                        if (this.currentStep === 19) document.getElementById("fakeDefaultContents").click();
                        document.getElementById("prevStepButton").disabled = false;
                    }, 1000);
                }, 100);
            } else if (stepNumber === 21) {
                setTimeout(() => {
                    if (this.currentStep === 21) document.querySelector(".toolbox").style.opacity = "1";
                }, 100);
            }
        },
        disableChatFakeMode() {
            this.setChatFakeMode(false);
            setTimeout(() => {
                document.getElementById("contents").style.pointerEvents = "";
                document.getElementById("sidebar").style.pointerEvents = "";
            }, 100);
        },
        forceToMainPath() {
            this.$router.push("/");
            this.userRoleUpdated = false;
        },
        updateSocketStatus(connected, loggedIn) {
            if (loggedIn) {
                if (this.loginTimer) clearInterval(this.loginTimer);
            }
            if (
                this.socketStatus.socket_connected === connected &&
                this.socketStatus.isUserLoggedViaSocket === loggedIn
            )
                return;

            this.setSocketStatus({
                socketConnected: connected,
                userLoggedin: loggedIn,
            });
        },
        startSocketIOConnection() {
            if (!this.isLoggedIn) return;
            if (this.socketConnected) return;
            if (this.connectionInProgress) return;
            this.connectionInProgress = true;
            log.info("startSocketIOConnection started");
            const candidateServer = getNextSocketServerCandidate(true);
            if (candidateServer) {
                log.warn("current candidate server", candidateServer, this.$socket);
                this.connectedSocketServerId = candidateServer.instanceId;
                const socketIOAddress = candidateServer.socketIOAddress;
                const isProduction = process.env.PRODUCTION === "true";
                const transportConfigs =
                    !this.tabVisible && this.lastTabHiddenTS && Date.now() - this.lastTabHiddenTS >= 5000
                        ? {
                              transports: ["polling"],
                              upgrade: true,
                              autoConnect: true,
                              reconnect: false,
                              reconnection: false,
                          }
                        : {
                              transports: ["websocket", "polling"],
                              upgrade: false,
                              autoConnect: true,
                              reconnect: false,
                              reconnection: false,
                          };

                const socket = io.connect(socketIOAddress, {
                    query: "source=client&siteId=" + this.site._id + "&userId=" + this.user?._id,
                    secure: isProduction,
                    rejectUnauthorized: false,
                    timeout: 30000,
                    ...transportConfigs,
                });
                log.warn("socket->", socket);
                this.$socket = socket;
                this.setSocket(this.$socket);
                this.bindSocketEvents();

                this.$socket.on("connect_error", (e) => {
                    log.warn("socket.io event connect_error", e);
                    this.connectionInProgress = false;
                    markFailedSocketServer(candidateServer.instanceId);
                    this.updateSocketStatus(false, false);
                    setTimeout(() => {
                        this.startSocketIOConnection();
                    }, Math.ceil(Math.random() * 1000) + 1000);
                });

                this.$socket.on("error", (e, a) => {
                    this.connectionInProgress = false;
                    console.error(e);
                    log.warn("socket.io error", e, a);
                    markFailedSocketServer(candidateServer.instanceId);
                    this.updateSocketStatus(false, false);

                    setTimeout(() => {
                        this.startSocketIOConnection();
                    }, Math.ceil(Math.random() * 1000) + 1000);
                });
                // this.init();
                log.debug("this.$socket", this.$socket, this);
            } else {
                this.connectionInProgress = false;
                log.warn("There is no socket server candidate , trying later");
                this.updateSocketStatus(false, false);
                setTimeout(() => {
                    this.startSocketIOConnection();
                }, Math.min(3000, this.socketTryCount * 100));
                this.socketTryCount += 1;
            }
        },
        playNewVisitorSound: _.debounce(function (ids) {
            try {
                ids = ids.map((x) => x.visitorId);
                if (ids.some((x) => !this.newVisitorSoundPlayed.includes(x)) && this.userInteracted)
                    newVisitorSound().play();
                this.newVisitorSoundPlayed = _.uniq([...this.newVisitorSoundPlayed, ...ids]);
            } catch (e) {
                log.error("playNewVisitorSound error", e);
            }
        }, 1000),
        closingAppAlert() {
            const message = {
                en: "You are about to close the Supsis application",
                tr: "Supsis uygulamasını kapatmak üzeresiniz.",
                ru: "Вы собираетесь закрыть приложение Supsis",
                de: "Sie sind dabei, die Supsis Anwendung zu schließen.",
                fr: "Vous êtes sur le point de fermer l'application Supsis.",
                es: "Está a punto de cerrar la aplicación Supsis.",
                ar: "أنت على وشك إغلاق تطبيق Supsis.",
                it: "Stai per chiudere l'applicazione Supsis.",
            };

            const returnMessage = message[this.$i18n.locale];

            window.onbeforeunload = function (e) {
                if (!IS_DEV_ENVIRONMENT) {
                    e = e || window.event;
                    // For IE and Firefox prior to version 4
                    if (e) {
                        e.returnValue = returnMessage;
                    }
                    // For Safari
                    return returnMessage;
                }
            };
        },
        connectSupsisSupportTeam(initialMessage, largeMode = false, connectingUserId) {
            log("connectSupsisSupportTeam", window.supsis);
            const message = {
                en: "Hello, I want to get help about Supsis",
                tr: "Merhaba, Supsis hakkında yardım almak istiyorum.",
                ru: "Здравствуйте, я хочу получить помощь по поводу Supsis",
                de: "Hallo, ich möchte Hilfe zu Supsis erhalten",
                fr: "Bonjour, je souhaite obtenir de l'aide sur Supsis",
                es: "Hola, quiero obtener ayuda sobre Supsis",
                ar: "مرحبًا ، أريد الحصول على مساعدة حول Supsis.",
                it: "Ciao, voglio ricevere aiuto su Supsis.",
            };

            const hide = this.$message.loading(this.$t("message.liveSupportStartingWait"), 0);

            const bubbles = {
                development: `http://localvisitor.supsis.live/loader.js`,
                stage: "https://betavisitor.supsis.live/static/js/loader.js",
                production: "https://visitor.supsis.live/static/js/loader.js",
            };
            window.supsis =
                window.supsis ||
                function () {
                    (supsis.q = supsis.q || []).push(arguments);
                };
            window.supsis.l = +new Date();
            const supsis = window.supsis;
            supsis("open");

            const userName = this.user && this.user.name ? this.user.name : "";
            const userEmail = this.user && this.user.email ? this.user.email : "";
            const userPhone = this.user && this.user.phone ? this.user.phone : "";
            const siteAddress = this.site && this.site.siteAddress ? this.site.siteAddress : "";

            supsis("setUserData", {
                name: userName,
                email: userEmail,
                phone: userPhone,
            });
            supsis("setContactProperty", {
                PanelLinki: siteAddress,
            });

            supsis("login", {
                initialMessage: initialMessage || message.tr,
                loginData: {
                    fullname: userName,
                    email: userEmail,
                    connectingUserId,
                },
                connectingUserId,
            });
            supsis("ready", () => {
                hide();
            });
            if (largeMode) supsis("setWindowSize", "large");
            supsis("onMinimized", (s) => s("destroy"));
            const src = bubbles[process.env.ENVIRONMENT];
            let scriptElement = document.createElement("script");
            scriptElement.setAttribute("src", src);
            scriptElement.setAttribute("async", "true");
            scriptElement.setAttribute("defer", "true");
            scriptElement.setAttribute("id", "previewScript");
            document.body.appendChild(scriptElement);
        },
        removeNewVisitorPlayedSound(ids) {
            this.newVisitorSoundPlayed = this.newVisitorSoundPlayed.filter((x) => !ids.includes(x));
        },
        retryFetch(prefix, promiseFn, maxRetry = 3) {
            return new Promise((resolve, reject) => {
                promiseFn()
                    .then((data) => {
                        log(`retryFetch completed (${prefix}) => remaining retry count = ${maxRetry}`);
                        resolve(data);
                    })
                    .catch((e) => {
                        if (maxRetry > 0) {
                            setTimeout(() => {
                                log(`retryFetch failed (${prefix}) => remaining retry count = ${maxRetry - 1}`);
                                this.retryFetch(prefix, promiseFn, maxRetry - 1);
                            }, 2000);
                        } else {
                            reject(e);
                        }
                    });
            });
        },
        async getSiteSubscribeDatas() {
            log("getSiteSubscribeDatas", {});
            this.retryFetch("getSitePlans", () => this.getSitePlans(), 50);
            await this.retryFetch("getSiteCurrentPlans", () => this.getSiteCurrentPlans(), 50);
            this.retryFetch("getSiteFeatures", () => this.getSiteFeatures(), 50);
        },
        fetchStartupDatas(source) {
            log("fetchStartupDatas", { source });
            this.retryFetch("getSubjects", () => this.getSubjects({ perPage: 500, page: 1 }), 3);
            this.retryFetch("getCustomLoginFormInputs", () => this.getCustomLoginFormInputs(), 3);
            this.retryFetch("getTags", () => this.getTags(), 3);
            this.retryFetch("getContents", () => this.getContents({ perPage: 1000, page: 1 }), 3);
            this.retryFetch("getCustomerDomainsIfNeed", () => this.getCustomerDomainsIfNeed(), 3);
            this.retryFetch("getBillingDetail", () => this.getBillingDetail(), 3);
            this.retryFetch("getContactProperty", () => this.getContactProperty(), 50);
            this.retryFetch("listSegments", () => this.listSegments(), 3);
            this.retryFetch(
                "listChannels",
                () => {
                    return this.listChannels().then((channels) => {
                        const businessChannels = channels.filter((channel) => channel.type === "whatsapp-business-api");
                        businessChannels.forEach((channel) => {
                            this.getTemplates(channel._id);
                        });
                    });
                },
                3
            );
            this.retryFetch("getWallet", () => this.getBalance(), 3);
            this.retryFetch("workflows", () => this.getWorkFlows(), 3);
            this.retryFetch("getConversationStatistics", () => this.getConversationStatistics(), 3);
            this.retryFetch("getBannedWords", () => this.getBannedWords({}), 3);
            this.retryFetch("getAgentsMailAddressList", () => this.getAgentsMailAddressList({}), 3);
            this.retryFetch("getStorageDetail", () => this.getStorageDetail({}), 3);
            this.retryFetch("fetchCustomerTables", () => this.fetchCustomerTables(), 3);
            this.retryFetch("syncAutomations", () => this.syncAutomations(), 3);
            this.retryFetch("syncModules", () => this.syncModules(), 3);
            this.retryFetch("listStories", () => this.listStories(), 3);
            this.retryFetch("syncForms", () => this.syncForms(), 3);
            this.retryFetch("fetchFilters", () => this.fetchFilters("page.chat-list"), 3);
            this.getSiteSubscribeDatas();
            this.getCustomerDomainsIfNeed();
        },
        listPrivateChats() {
            if (!!this.user && !!this.site) {
                sendSocketMessage("team list-chats", {
                    siteId: this.site._id,
                    userId: this.user._id,
                });
            }
        },
        emailDomainIsCreated() {
            if (!!this.user && !!this.site) {
                sendSocketMessage("email integration_is_enable", {
                    siteId: this.site._id,
                    domain: this.emailDomain,
                    userId: this.user._id,
                });
            }
        },
        listPrivateSelectedChatChats() {
            if (!!this.user && !!this.site) {
                sendSocketMessage("team list-chats-selected-chat", {
                    siteId: this.site._id,
                    userId: this.user._id,
                });
            }
        },
        listUnreadMessageCount() {
            if (!!this.user && !!this.site) {
                sendSocketMessage("team list-unread-chats", {
                    siteId: this.site._id,
                    userId: this.user._id,
                });
            }
        },
        playNotificationSound: _.debounce(function (isReceive = false) {
            if (isReceive) {
                if (this.privateChatIsMuted) return;
                sendDesktopNotification(this.$t("message.betweenRepresentative"), 2);
                if (this.userInteracted) {
                    newPrivateMessage(this.getMessageChatType).play();
                }
            }
        }, 1000),
        handleWidgetClose() {
            this.widget = {
                ...this.widget,
                visible: false,
                title: "",
                url: "",
                mode: "url",
                html: "",
            };
        },
    },
    mounted() {
        localStorage.setItem("first-visit", "false");
        Sentry.setTags({
            version: version.versionStr,
            buildNumber: version.buildNumber,
        });
        moment.locale(this.$i18n.locale);
        this.init();
        // this.closingAppAlert();

        const backendLogoutSub = systemMessageStream.pipe(filter((x) => x.type === "logout")).subscribe(() => {
            this.disconnectSocket(undefined, "backend-logout");

            this.$router.push("/login");
        });
        const focusChatSub = systemMessageStream
            .pipe(filter((x) => x.type === SystemMessageTypes.FOCUS_CHAT))
            .subscribe((data) => {
                this.focusChat(data.payload.conversationId);
            });
        //dışarıdan trigger'lanan zil çalma kanalı. çalağı ziller arası min zaman 5sndir
        const beepPlayerSub = systemMessageStream.pipe(filter((x) => x.type === "play-beep")).subscribe(() => {
            this.playBeep();
        });

        const connectSupportTeamSub = systemMessageStream
            .pipe(filter((x) => x.type === SystemMessageTypes.CONNECT_SUPPORT_TEAM))
            .subscribe((x) => {
                log("CONNECT_SUPPORT_TEAM request received");
                this.connectSupsisSupportTeam(
                    x?.payload?.initialMessage,
                    x?.payload?.largeMode,
                    x?.payload?.connectingUserId
                );
            });

        const tabVisibilitySub = systemMessageStream
            .pipe(
                filter((x) => x.type === SystemMessageTypes.TAB_VISIBILITY_CHANGE),
                map((x) => x.payload)
            )
            .subscribe((payload) => {
                this.changeTabVisibilityChange(payload);
            });

        const sub = socketMessageStream.pipe(filter((x) => ["message"].includes(x.cmd))).subscribe((x) => {
            if (x.cmd === "message") {
                if (!!x.data.isTeamChat) {
                    this.updateChatLastMessage({ chatId: x.data.conversationId, message: x.data.message });
                    if (
                        x.data.userId.toString() !== this.user._id.toString() &&
                        (!this.teamChatVisible || this.selectedChat._id !== x.data.conversationId)
                    ) {
                        this.increaseUnreadTeamMessageCount(x.data.conversationId);
                    }
                    this.getMessageChatType = this.chats.find((q) => q._id === x.data.conversationId).meta.chatType;
                    this.playNotificationSound(
                        x.data.userId !== this.user._id && this.isNotificationMute(x.data.conversationId)
                    );
                }
                if (x.data.type !== "customer-typing") {
                    this.addMessageToChatHistory({
                        conversationId: x.data.conversation || x.data.conversationId,
                        message: x.data,
                    });
                }
            }
        });
        const resfreshSub = socketMessageStream.pipe(filter((x) => ["refresh"].includes(x.cmd))).subscribe((x) => {
            window.location.reload();
        });
        const showSocketMessage = socketMessageStream
            .pipe(filter((x) => ["show_error"].includes(x.cmd)))
            .subscribe((x) => {
                if (x.cmd === "show_error") {
                    let { message, payload, type, level } = x.data;
                    if (type === "blocked_message") {
                        const pendingMessages = JSON.parse(localStorage.getItem("pending-messages") || "[]");
                        localStorage.setItem(
                            "pending-messages",
                            JSON.stringify(
                                pendingMessages.filter((message) => message.uniqMessageId !== payload.uniqMessageId)
                            )
                        );
                    }

                    level = level || "error";
                    if (level === "error") {
                        this.$notification.error({
                            message: "Hata",
                            description: message, // socket tarafından i18n key alınıp çeviri yapılması gerekli
                        });
                    } else if (level === "warning") {
                        this.$notification.warning({
                            message: "Uyarı",
                            description: message, // socket tarafından i18n key alınıp çeviri yapılması gerekli
                        });
                    } else if (level === "info") {
                        this.$notification.info({
                            message: "Bilgi",
                            description: message, // socket tarafından i18n key alınıp çeviri yapılması gerekli
                        });
                    }
                }
            });

        const removePendingMessageSub = socketMessageStream
            .pipe(filter((x) => ["remove_pending_message"].includes(x.cmd)))
            .subscribe((x) => {
                if (x.cmd === "remove_pending_message") {
                    const pendingMessages = JSON.parse(localStorage.getItem("pending-messages") || "[]");
                    localStorage.setItem(
                        "pending-messages",
                        JSON.stringify(
                            pendingMessages.filter((message) => message.uniqMessageId !== x.data.uniqMessageId)
                        )
                    );
                }
            });
        //workaround status sync makro için doğru mu değilmi bu yüzden eklendi
        const statusSyncCompareTimer = setInterval(() => {
            if (this.user && this.site) {
                sendSocketMessage("user status-sync", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    status: this.user.status,
                    isOnline: true,
                });
            }
        }, 60 * 1000);

        this.notificationCountFetchFirst = true;
        // bakiye için her 10 saniyede bir istek atıyor.
        const countTimerId = setInterval(() => {
            const isValid = this.user && this.isLoggedIn && !!this.lastNotification && this.tabVisible;
            const isFirst = this.user && this.isLoggedIn && this.notificationCountFetchFirst;
            // console.log("count_debug", {
            //     isValid,
            //     isFirst,
            //     isLoggedIn: this.isLoggedIn,
            //     lastNotification: this.lastNotification,
            //     tabVisible: this.tabVisible,
            //     notificationCountFetchFirst: this.notificationCountFetchFirst,
            // });
            if (isValid || isFirst) {
                this.lastNotification = null;
                this.notificationCountFetchFirst = false;
                ACTIVITY_API.NOTIFICATION.getNotificationCount().then((data) => {
                    this.setNotificationCount(data.count);
                });
            }
            if (this.isLoggedIn && this.tabVisible) {
                if (Math.random() < 0.2 && this.anyWhatsappChannelExists) this.getBalance();
            }
        }, 3 * 1000);

        const shouldSelectPlanSub = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.SHOULD_SELECT_PLAN))
            .subscribe((x) => {
                if (this.user) {
                    this.shouldSelectPlanEnabled = this.shouldSelectPlan.enabled;
                    this.$router.push("/settings/billing");
                }
            });

        const suspendSub = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.SHOULD_PAYMENTS))
            .subscribe((x) => {
                if (this.user) this.suspendModalVisible = true;
            });

        const openWidgetSub = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.OPEN_WIDGET))
            .subscribe((x) => {
                this.widget = {
                    title: x.payload.title,
                    url: x.payload.url,
                    fullScreen: x.payload.fullScreen,
                    visible: true,
                    mode: x.payload.mode,
                    html: x.payload.html,
                };
            });

        this.cancels.push(() => {
            clearInterval(statusSyncCompareTimer);

            backendLogoutSub.unsubscribe();
            tabVisibilitySub.unsubscribe();
            beepPlayerSub.unsubscribe();
            sub.unsubscribe();
            focusChatSub.unsubscribe();
            connectSupportTeamSub.unsubscribe();
            resfreshSub.unsubscribe();
            showSocketMessage.unsubscribe();
            removePendingMessageSub.unsubscribe();
            shouldSelectPlanSub.unsubscribe();
            suspendSub.unsubscribe();
            clearInterval(countTimerId);
            openWidgetSub.unsubscribe();
        });

        window.addEventListener("keydown", (e) => {
            let val = e.key;
            if (val === "M" || val === "m") {
                val = val.toLowerCase();
            }
            if ("m" === val && e.ctrlKey) {
                this.togglePrivateChatVisibleState();
                setTimeout(() => {
                    const elem = document.getElementById("privateChatUserInput");
                    if (!!elem) elem.focus();
                }, 100);
            }
            if ("Escape" === e.key || "Esc" === e.key) {
                publishMessage("esc-keydown", null);
                if (document.body.classList.contains("viewer-open")) return;

                // Post-it tahtası açıksa önce onu kapat
                if (this.$store.state.app.postitBoardVisible) {
                    this.$store.commit("app/SET_POSTIT_BOARD_VISIBLE", false);
                    return;
                }

                this.setChatFakeMode(false);
                setTimeout(() => {
                    document.getElementById("contents").style.pointerEvents = "";
                    document.getElementById("sidebar").style.pointerEvents = "";
                }, 100);
                if (this.isHeaderPopoversOpen) {
                    this.headerPopover.isProfileMenuPopoverOpen = false;
                    this.headerPopover.isHelpMenuPopoverOpen = false;
                    this.headerPopover.isSettingPopoverOpen = false;
                } else if (this.isChatHistoryOpen) {
                    this.toggleChatHistory(false);
                }
            }
        });
        const beforeUnloadCallback = (e) => {
            this.disconnectSocket(undefined, "beforeunload");
        };
        const clickCallback = () => {
            if (!this.userInteracted) {
                this.setUserInteracted();
            }
        };
        window.addEventListener("beforeunload", beforeUnloadCallback);
        window.addEventListener("click", clickCallback);
        this.cancels.push(() => {
            window.removeEventListener("beforeunload", beforeUnloadCallback);
            window.removeEventListener("click", clickCallback);
        });
        this.resizeListener();
    },
    beforeDestroy() {
        log("beforeDestroy called");
        this.cancels.forEach((x) => x());
        this.disconnectSocket(undefined, "before-destroy");
    },
    updated() {
        try {
            if (this.$route.query.debug) this.setDebugMode();
            if (this.language && this.language.code !== this.$i18n.locale) {
                log("lang diff", this.language.code, this.$i18n.locale);
                this.$i18n.locale = this.language.code;
            }
        } catch (e) {
            log.error(e);
        }
    },
    watch: {
        tabVisible(isActive) {
            log("supsis tab visibility changed", isActive);
            if (isActive && _.get(window, "socket.io.opts.transports.0") === "polling") {
                log.warn("supsis tab visibility is activated and socket is polling, reconnecting socket via websocket");
                window.socket.disconnect();
            }
        },
        $route(to, from) {
            const routerPush = (path) => this.$router.push(path);
            pathPermissionChecker(to, from, this.checkPermission, this.$message.error, routerPush, this);
            this.setRoute({
                path: to.path,
                query: {
                    ...to.query,
                },
                params: {
                    ...to.params,
                },
                fullPath: to.fullPath,
                name: to.name,
                meta: to.meta,
            });
        },
        socketConnected(isConnected) {
            log("socketConnected changed", { isConnected });
            this.resetChatHistory();
            if (isConnected) {
                this.fetchStartupDatas("socketConnected");
                this.emailDomainIsCreated();
            }
        },
        lastLoggedUserId(userId) {
            if (userId && this.site && this.site._id != null) {
                this.resetChatHistory();
                this.fetchStartupDatas("lastLoggedUserId");
            }
        },
        user: {
            handler(newValue) {
                if (newValue && this.site && this.site._id != null) {
                    this.lastLoggedUserId = _.get(newValue, "_id");
                    log("user changed", newValue);
                    this.login();
                    Sentry.setUser({ email: newValue.email, name: newValue.name });
                    this.subscribePushNotifications();
                }
                if (_.isEmpty(newValue)) {
                    this.lastLoggedUserId = null;
                    this.suspendModalVisible = false;
                    this.shouldSelectPlanEnabled = false;
                }
            },
            deep: true,
        },
        adminUser: {
            handler(newValue) {
                if (newValue) {
                    this.lastLoggedUserId = _.get(newValue, "_id");
                    log("adminUser changed", newValue);

                    Sentry.setUser({ email: newValue.email, name: newValue.name });
                    getErrorReports()
                        .then((response) => {
                            this.setBugExist(
                                !!response.data.errors &&
                                    response.data.errors.length > 0 &&
                                    response.data.errors.some((x) => !x.solved !== false)
                            );
                        })
                        .catch((x) => {
                            console.error("resEr in app get error failed", { x });
                        });
                }
                if (_.isEmpty(newValue)) {
                    this.lastLoggedUserId = null;
                }
            },
            deep: true,
        },
        site: {
            handler(newValue, oldVal) {
                if (newValue.suspend !== oldVal.suspend) {
                    this.suspendModalVisible = newValue.suspend;
                }
                if (newValue && this.site && this.site._id != null) {
                    document.title = `Supsis - ${this.site.alias.charAt(0).toUpperCase() + this.site.alias.slice(1)}`;
                    this.login();
                    Sentry.setTags({
                        siteId: this.site._id,
                        siteName: this.site.clientAddress,
                        partner: _.get(this.site, "partner.name"),
                    });
                    if (!this.isAdmin) this.refreshAuthUser();
                }
            },
            deep: true,
        },
        isLoggedIn(newLoggedIn) {
            log("isLoggedIn changed", newLoggedIn);
            if (newLoggedIn) {
                this.getPermissions({ perPage: 500, page: 1 }); //permission'lar olmayınca sidebar çizilemiyor o yüzden çağırıyoruz.
                log("new login received");
                this.updateSocketStatus(false, false);

                this.startSocketIOConnection();
                this.fetchStartupDatas("isLoggedIn");
            } else {
                this.disconnectSocket(false, "logout");
            }
        },
        zoom(newValue) {
            log("zoom value changed", newValue);
            document.body.style.zoom = newValue + "%";
            this.$forceUpdate();
        },

        language() {
            log("language changed");
            this.$forceUpdate();
        },
        plansAvailable(val) {
            if (this.shouldSelectPlan.enabled && !this.isAdmin && val) {
                this.$router.push("/settings/billing");
            }
        },
        "shouldSelectPlan.enabled": {
            handler() {
                this.shouldSelectPlanEnabled = this.shouldSelectPlan.enabled;
            },
            immediate: true,
        },
    },
};
</script>

<style lang="scss">
//#app{
//    width: 100%;
//    height: 100%;
//    overflow: hidden;
//}
#ls-trigger {
    display: none !important;
}

.multiSessionClass {
    .ant-modal-mask {
        background-color: rgba(0, 0, 0, 0.85) !important;
    }
}

#fastWizardModal {
    .ant-modal {
        width: 100vw !important;
        height: 100vh !important;
    }
}

.fastWizardModal {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999999;

    .ant-modal-wrap {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .ant-modal-mask {
        background-color: rgba(0, 0, 0, 0.85) !important;
    }

    .ant-modal {
        width: 90vw !important;
        margin: auto;
        top: 0 !important;
        padding-bottom: 0 !important;
    }

    .ant-modal-body {
        padding: 0 !important;
        height: 100vh !important;
    }
}

@media (max-width: 700px) {
    #fastWizardModal {
        scale: 1;
        position: fixed;
        top: 0;
        left: 0;
        z-index: 99999999;

        .ant-modal-wrap {
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .ant-modal-mask {
            background-color: rgba(0, 0, 0, 0.85) !important;
        }

        .ant-modal {
            width: 100vw !important;
            height: 100vh !important;
            margin: 0 !important;
            padding: 0 !important;
            top: 0 !important;
            -webkit-transform: none !important;
            transform: none !important;
        }

        .ant-modal-body {
            height: 100vh !important;
            overflow-y: auto;
        }
    }
}

.fastWizardModal {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999999;

    .ant-modal-wrap {
        display: block;
        justify-content: center;
        align-items: center;
    }
}
</style>
